import React, { ReactNode, useMemo } from "react";
import { Box, Typography, Stack } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import { truckTypeLabel } from "./shared";
import { MaterialOrder } from "@/components/planning/Project";
import { xBFormatter } from "@/components/calendar/dialogs/OrderHistoryDialog";
import { createDeliveryText } from "@/components/planning/editors/MaterialTab";
import { BorderColor, OpenInNew, OpenInNewOff } from "@mui/icons-material";

export function SummaryContainer({ children }: { children: ReactNode }) {
  return <Box sx={{ mb: 1, pb: 1, borderBottom: 2, borderColor: "primary.light" }}>{children}</Box>;
}

export function MaterialSummary({
  order,
  isBauhof,
  isBaustellendienst,
  includeAmount = false,
}: {
  order: MaterialOrder;
  isBauhof: boolean;
  isBaustellendienst: boolean;
  includeAmount?: boolean;
}) {
  if (isBaustellendienst) {
    return <Typography fontStyle="italic">Nur Baustellendienst</Typography>;
  }

  if (!order.article) {
    return <Typography fontStyle="italic">Kein Material</Typography>;
  }

  if (isBauhof) {
    return (
      <Stack>
        <Typography>
          {order.amount}x {order.article.name}
        </Typography>

        {order.additionalArticles.map((a, idx) =>
          a.deleted || !a.article || a.amount === 0 ? null : (
            <Typography key={a.article?.id ?? a.article?.number ?? idx}>
              {a.amount}x {a.article.name}
            </Typography>
          ),
        )}
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {!includeAmount ? null : order.amount === -1 ? (
        <Typography>Keine feste Menge (Baustellendienst)</Typography>
      ) : (
        <Typography>
          {order.amount || 0} {order.roundsUnit ? (order.amount > 1 ? "Touren" : "Tour") : (order.article?.unit ?? "t")}
        </Typography>
      )}

      <Typography>{order.article.name}</Typography>

      <Typography color="text.secondary">{order.article.number}</Typography>

      <Typography color="text.secondary">
        {[
          order.article.plantName,
          order.article.supplierName,
          !!order.position
            ? order.position?.deliveryType.name
            : order.manualDAP
              ? "Frei Bau"
              : /(anlieferung|annahme|aufbruch|aushub|pechhal)/i.test(order.article.name)
                ? "Anlieferung"
                : "Abholung",
        ]
          .filter(Boolean)
          .join(" - ")}
      </Typography>

      {!order.position ? (
        <Typography fontStyle="italic" color="error">
          Unbepreister Artikel!
        </Typography>
      ) : null}
    </Stack>
  );
}

export function MengeSummary({ order, isDispo }: { order: MaterialOrder; isDispo: boolean }) {
  if (order.amount === -1) {
    return <Typography>Keine feste Menge (Baustellendienst)</Typography>;
  }

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <Typography>
        {order.amount || 0} {order.roundsUnit ? (order.amount > 1 ? "Touren" : "Tour") : (order.article?.unit ?? "t")}
      </Typography>
      {order.showAmountInTransplan === true || (order.showAmountInTransplan === null && (order.amount || 0) < 27) ? (
        <OpenInNew fontSize="inherit" />
      ) : (
        <OpenInNewOff fontSize="inherit" />
      )}
    </Box>
  );
}

export function UhrzeitSummary({ order }: { order: MaterialOrder }) {
  if (!order.firstUnload) {
    return <Typography>Keine Uhrzeit gesetzt (anschließend)</Typography>;
  }

  return (
    <Typography>
      <Typography component="span" fontStyle="italic">
        Erster LKW:
      </Typography>{" "}
      {!order.firstUnload.startsWith("x")
        ? order.firstUnload + " Uhr"
        : order.firstUnload.startsWith("xB")
          ? xBFormatter(order.firstUnload, "INFO", true)
          : order.firstUnload === "xAM"
            ? "vormittags"
            : order.firstUnload === "xPM"
              ? "nachmittags"
              : ""}
    </Typography>
  );
}

export function TaktSummary({ order }: { order: MaterialOrder }) {
  const totalAmount = useMemo(
    () => (order.transportInformation ?? []).reduce((carry, ti) => carry + (ti.amount ?? 0), 0),
    [order.transportInformation],
  );

  const hasSiteWork = useMemo(() => (order.transportInformation ?? []).some((x) => !!x.siteWork), [order.transportInformation]);

  const showTakt = totalAmount > 0;
  const showSiteWork = hasSiteWork && order.duration !== "INVALID";

  return showTakt || showSiteWork ? (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {showTakt ? <Typography>{order.takt > 0 ? `${order.takt} min Takt` : "ohne Takt"}</Typography> : null}

      {showSiteWork && showTakt ? <Typography color="text.secondary">-</Typography> : null}

      {showSiteWork ? (
        <Typography>Baustellendienst: {order.duration === "FULL" ? "Ganztägig" : order.duration === "HALF" ? "Halbtags" : "Eine Runde"}</Typography>
      ) : null}
    </Stack>
  ) : null;
}

export function FuhrparkSummary({ order, wizardIndex = -1 }: { order: MaterialOrder; wizardIndex?: number }) {
  const ti = order.transportInformation ?? [];
  if (ti.length === 0) {
    return <Typography>Kein LKW geplant (z.B. eigenständige Abholung)</Typography>;
  }

  const tt = createDeliveryText(order["transportInformation"], order["takt"], order["duration"], true);

  return (
    <Stack>
      {(tt ?? []).map((ti, idx) => (
        <Stack
          key={idx}
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
            color: wizardIndex === idx ? "#6d0056ff" : "black",
            justifyContent: "flex-start",
          }}
        >
          {wizardIndex === idx ? <BorderColor fontSize="small" color="action" /> : null}
          {ti}
        </Stack>
      ))}
    </Stack>
  );
}

export function SonstigesSummary({ order, expertMode, isDispo }: { order: MaterialOrder; expertMode: boolean; isDispo: boolean }) {
  return (
    <Stack>
      <Typography>
        <Typography component="span" fontStyle="italic">
          Lieferort:
        </Typography>{" "}
        {order.easyTarget === "LAGER" ? "Lagerplatz" : "Baustelle"}
      </Typography>

      {expertMode || order.begleitschein ? (
        <Typography>
          <Typography component="span" fontStyle="italic">
            Begleitschein:
          </Typography>{" "}
          {order.begleitschein ? "Ja" : "Nein"}
        </Typography>
      ) : null}

      {order.comment ? (
        <Typography
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {order.comment}
        </Typography>
      ) : (
        <Typography color="text.secondary">Ohne {isDispo ? "Fahrer-" : ""}Kommentar</Typography>
      )}
      {!isDispo ? null : order.transportComment ? (
        <Typography
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {order.transportComment}
        </Typography>
      ) : (
        <Typography color="text.secondary">Ohne Dispo-Kommentar</Typography>
      )}
    </Stack>
  );
}
